/**
 * StaffDutyDialog — 本日勤務管理モーダル
 *
 * スタッフを「本日出勤」と「オフ」にDnDで振り分け、優先度順を並べ替える。
 * priority >= 1000 → 本日出勤、< 1000 → オフ
 *
 * 実装: 2つの SortableContext + 各エリアを useDroppable でラップ。
 * アイテムがゾーンをまたぐ場合、ドロップ先のゾーンコンテナ or アイテムで判定。
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemText, Typography, Box, Chip,
  CircularProgress, Alert, Divider,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext, DragOverlay, PointerSensor, TouchSensor,
  useSensor, useSensors, useDroppable,
  rectIntersection,
} from '@dnd-kit/core';
import type { DragStartEvent, DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { isOnDuty, toOnDuty, toOffDuty } from '../utils/staffPriority';
import { saveStaffPrioritiesBulk } from '../api/dispatchService';

// --- ソート可能なスタッフアイテム ---
function SortableStaffItem({ staff, index, zone }: { staff: any; index: number; zone: 'on' | 'off' }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: String(staff.id), data: { zone, staff } });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        mb: 0.5,
        py: 0.5,
        backgroundColor: isDragging ? '#f5f5f5' : (zone === 'on' ? '#e8f5e9' : '#fff'),
        cursor: 'grab',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Chip
        label={index + 1}
        size="small"
        color={zone === 'on' ? 'success' : 'default'}
        sx={{ mr: 1, minWidth: 28, height: 22 }}
      />
      {staff.bg_color ? (
        <Chip
          label={staff.staff_name}
          size="small"
          sx={{
            backgroundColor: staff.bg_color,
            color: staff.text_color || '#fff',
            fontWeight: 600,
            mr: 1,
          }}
        />
      ) : (
        <ListItemText
          primary={staff.staff_name}
          primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
        />
      )}
    </ListItem>
  );
}

// --- ドロップ可能なゾーンコンテナ ---
function DroppableZone({ id, children, bgColor }: { id: string; children: React.ReactNode; bgColor: string }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 48,
        p: 0.5,
        borderRadius: 1,
        backgroundColor: bgColor,
        border: isOver ? '2px dashed #1976d2' : '2px dashed transparent',
        transition: 'border-color 0.2s',
      }}
    >
      {children}
    </Box>
  );
}

interface StaffDutyDialogProps {
  open: boolean;
  onClose: () => void;
  staffList: any[];
  onSaved?: () => void;
}

export default function StaffDutyDialog({ open, onClose, staffList, onSaved }: StaffDutyDialogProps) {
  const [onDutyList, setOnDutyList] = useState<any[]>([]);
  const [offDutyList, setOffDutyList] = useState<any[]>([]);
  const [activeStaff, setActiveStaff] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } }),
  );

  // ダイアログが開くたびにスタッフを振り分け
  useEffect(() => {
    if (!open || !Array.isArray(staffList)) return;
    const active = staffList.filter(s => s.is_active === 1 && !s.is_deleted && s.id !== 1);
    const onDuty = active
      .filter(s => isOnDuty(s.priority))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    const offDuty = active
      .filter(s => !isOnDuty(s.priority))
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
    setOnDutyList(onDuty);
    setOffDutyList(offDuty);
    setError(null);
  }, [open, staffList]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const id = String(event.active.id);
    const staff = onDutyList.find(s => String(s.id) === id) || offDutyList.find(s => String(s.id) === id);
    setActiveStaff(staff || null);
  }, [onDutyList, offDutyList]);

  // アイテムがどのゾーンに属するか判定
  const findZone = useCallback((staffId: string): 'on' | 'off' | null => {
    if (onDutyList.some(s => String(s.id) === staffId)) return 'on';
    if (offDutyList.some(s => String(s.id) === staffId)) return 'off';
    return null;
  }, [onDutyList, offDutyList]);

  // ドロップ先のゾーンを判定（アイテム or コンテナ）
  const resolveOverZone = useCallback((overId: string): 'on' | 'off' | null => {
    if (overId === 'zone-on') return 'on';
    if (overId === 'zone-off') return 'off';
    return findZone(overId);
  }, [findZone]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveStaff(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    const activeZone = findZone(activeId);
    const overZone = resolveOverZone(overId);

    if (!activeZone || !overZone) return;

    const activeList = activeZone === 'on' ? onDutyList : offDutyList;
    const staff = activeList.find(s => String(s.id) === activeId);
    if (!staff) return;

    if (activeZone === overZone) {
      // 同じゾーン内のソート
      const setter = activeZone === 'on' ? setOnDutyList : setOffDutyList;
      const oldIndex = activeList.findIndex(s => String(s.id) === activeId);
      const newIndex = activeList.findIndex(s => String(s.id) === overId);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        setter(arrayMove(activeList, oldIndex, newIndex));
      }
    } else {
      // ゾーン間移動
      const fromSetter = activeZone === 'on' ? setOnDutyList : setOffDutyList;
      const toSetter = overZone === 'on' ? setOnDutyList : setOffDutyList;
      const toList = overZone === 'on' ? onDutyList : offDutyList;
      const newPriority = overZone === 'on' ? toOnDuty(staff.priority || 1) : toOffDuty(staff.priority || 1);

      // 元のリストから削除
      fromSetter(prev => prev.filter(s => String(s.id) !== activeId));

      // 先のリストに挿入（ドロップ先のアイテム位置 or 末尾）
      const overIndex = toList.findIndex(s => String(s.id) === overId);
      toSetter(prev => {
        const updated = [...prev.filter(s => String(s.id) !== activeId)];
        const insertAt = overIndex >= 0 ? overIndex : updated.length;
        updated.splice(insertAt, 0, { ...staff, priority: newPriority });
        return updated;
      });
    }
  }, [onDutyList, offDutyList, findZone, resolveOverZone]);

  // 保存
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const updates: { id: number; priority: number }[] = [];

      // 出勤エリア: 上位 = 大きい値（1000 + base）
      onDutyList.forEach((staff, index) => {
        const base = onDutyList.length - index;
        updates.push({ id: staff.id, priority: toOnDuty(base) });
      });

      // オフエリア: 上位 = 大きい値（1〜999）
      offDutyList.forEach((staff, index) => {
        const base = offDutyList.length - index;
        updates.push({ id: staff.id, priority: base });
      });

      // 一括保存 + 全タブへブロードキャスト
      saveStaffPrioritiesBulk(updates);

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Failed to save staff duty:', err);
      setError('保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const onDutyIds = onDutyList.map(s => String(s.id));
  const offDutyIds = offDutyList.map(s => String(s.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ pb: 1 }}>本日勤務</DialogTitle>
      <DialogContent dividers sx={{ px: 2, py: 1, overflow: 'auto' }}>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {/* 出勤エリア */}
          <Typography variant="caption" fontWeight={700} color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            🟢 本日出勤 ({onDutyList.length})
          </Typography>
          <DroppableZone id="zone-on" bgColor="#f1f8e9">
            <SortableContext items={onDutyIds} strategy={verticalListSortingStrategy}>
              {onDutyList.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, opacity: 0.6 }}>
                  スタッフをここにドラッグ
                </Typography>
              ) : (
                <List disablePadding>
                  {onDutyList.map((staff, index) => (
                    <SortableStaffItem key={staff.id} staff={staff} index={index} zone="on" />
                  ))}
                </List>
              )}
            </SortableContext>
          </DroppableZone>

          <Divider sx={{ my: 1.5, borderStyle: 'dashed', borderWidth: 2 }} />

          {/* オフエリア */}
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
            ⚪ オフ ({offDutyList.length})
          </Typography>
          <DroppableZone id="zone-off" bgColor="#fafafa">
            <SortableContext items={offDutyIds} strategy={verticalListSortingStrategy}>
              {offDutyList.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2, opacity: 0.6 }}>
                  スタッフをここにドラッグ
                </Typography>
              ) : (
                <List disablePadding>
                  {offDutyList.map((staff, index) => (
                    <SortableStaffItem key={staff.id} staff={staff} index={index} zone="off" />
                  ))}
                </List>
              )}
            </SortableContext>
          </DroppableZone>

          {/* ドラッグ中のゴースト表示 */}
          <DragOverlay>
            {activeStaff && (
              <ListItem
                sx={{
                  border: '1px solid #1976d2',
                  borderRadius: 1,
                  py: 0.5,
                  backgroundColor: '#e3f2fd',
                  boxShadow: 4,
                  cursor: 'grabbing',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                  <DragIndicatorIcon fontSize="small" />
                </Box>
                {activeStaff.bg_color ? (
                  <Chip
                    label={activeStaff.staff_name}
                    size="small"
                    sx={{
                      backgroundColor: activeStaff.bg_color,
                      color: activeStaff.text_color || '#fff',
                      fontWeight: 600,
                    }}
                  />
                ) : (
                  <Typography variant="body2" fontWeight={600}>
                    {activeStaff.staff_name}
                  </Typography>
                )}
              </ListItem>
            )}
          </DragOverlay>
        </DndContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>キャンセル</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : undefined}
        >
          {saving ? '保存中...' : '保存'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
